import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FileText, Download, Calendar, Filter, Printer, Mail,
  Building2, DollarSign, Users, AlertTriangle, TrendingUp, XCircle
} from 'lucide-react';
import { ResPDFGenerator } from '../../utils/pdfGenerator';

const Reports = () => {
  const [selectedReport, setSelectedReport] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [reportFormat, setReportFormat] = useState('pdf');

  const reportTypes = [
    {
      id: 'rental-summary',
      title: 'Rental Summary Report',
      description: 'Overview of all rental agreements, contracts, and payment status',
      icon: FileText,
      color: 'blue'
    },
    {
      id: 'financial',
      title: 'Financial Report',
      description: 'Revenue, expenses, and financial performance analysis',
      icon: DollarSign,
      color: 'green'
    },
    {
      id: 'occupancy',
      title: 'Occupancy Report',
      description: 'Property occupancy rates and vacancy analysis',
      icon: Building2,
      color: 'orange'
    },
    {
      id: 'tenant',
      title: 'Tenant Report',
      description: 'Tenant information, payment history, and contract details',
      icon: Users,
      color: 'purple'
    },
    {
      id: 'maintenance',
      title: 'Maintenance Report',
      description: 'Issues, maintenance requests, and repair costs',
      icon: AlertTriangle,
      color: 'red'
    },
    {
      id: 'performance',
      title: 'Performance Report',
      description: 'Property performance metrics and trends',
      icon: TrendingUp,
      color: 'amber'
    },
  ];

  const ReportCard = ({ report }) => {
    const Icon = report.icon;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-all cursor-pointer"
        onClick={() => setSelectedReport(report)}
      >
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-lg bg-${report.color}-100`}>
            <Icon className={`w-6 h-6 text-${report.color}-600`} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{report.title}</h3>
            <p className="text-sm text-gray-600 mb-4">{report.description}</p>
            <div className="flex gap-2">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedReport(report);
                }}
                className="px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Generate
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  alert(`Schedule report: ${report.title}`);
                }}
                className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium flex items-center gap-2"
              >
                <Calendar className="w-4 h-4" />
                Schedule
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Reports</h2>
            <p className="text-sm text-gray-600 mt-1">
              Generate and download comprehensive real estate reports
            </p>
          </div>
          <div className="flex gap-3">
            <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm font-medium">
              <Filter className="w-4 h-4" />
              Filters
            </button>
          </div>
        </div>
      </div>

      {/* Report Types Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {reportTypes.map((report) => (
          <ReportCard key={report.id} report={report} />
        ))}
      </div>

      {/* Report Generation Modal */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full"
          >
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Generate {selectedReport.title}</h3>
              <button
                onClick={() => setSelectedReport(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <XCircle className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Date Range</label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Start Date</label>
                    <input
                      type="date"
                      value={dateRange.startDate}
                      onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">End Date</label>
                    <input
                      type="date"
                      value={dateRange.endDate}
                      onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Format</label>
                <div className="flex gap-3">
                  <button 
                    onClick={() => setReportFormat('pdf')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${
                      reportFormat === 'pdf' 
                        ? 'bg-amber-600 text-white hover:bg-amber-700' 
                        : 'border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <FileText className="w-4 h-4" />
                    PDF
                  </button>
                  <button 
                    onClick={() => setReportFormat('excel')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${
                      reportFormat === 'excel' 
                        ? 'bg-amber-600 text-white hover:bg-amber-700' 
                        : 'border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <FileText className="w-4 h-4" />
                    Excel
                  </button>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6 flex gap-3">
                <button 
                  onClick={() => {
                    if (selectedReport.id === 'occupancy') {
                      // Generate property report
                      const mockProperties = [
                        { internalId: 'LOC-RYD-001', name: 'Riyadh Main Store', type: 'store', city: 'Riyadh', status: 'occupied', area: 500, monthlyRent: 15000 },
                        { internalId: 'LOC-JED-001', name: 'Jeddah Warehouse Complex', type: 'warehouse', city: 'Jeddah', status: 'occupied', area: 2000, monthlyRent: 25000 },
                      ];
                      const pdf = ResPDFGenerator.generatePropertyReport(mockProperties);
                      pdf.save(`report_${selectedReport.id}_${dateRange.startDate}_${dateRange.endDate}.pdf`);
                    } else {
                      alert(`Generating ${selectedReport.title} for ${dateRange.startDate} to ${dateRange.endDate} in ${reportFormat.toUpperCase()} format`);
                    }
                    setSelectedReport(null);
                  }}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2 flex-1"
                >
                  <Download className="w-4 h-4" />
                  Generate & Download
                </button>
                <button 
                  onClick={() => {
                    alert(`Email report: ${selectedReport.title}`);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  Email
                </button>
                <button 
                  onClick={() => {
                    if (selectedReport.id === 'occupancy') {
                      const mockProperties = [
                        { internalId: 'LOC-RYD-001', name: 'Riyadh Main Store', type: 'store', city: 'Riyadh', status: 'occupied', area: 500, monthlyRent: 15000 },
                      ];
                      const pdf = ResPDFGenerator.generatePropertyReport(mockProperties);
                      pdf.autoPrint();
                      pdf.output('dataurlnewwindow');
                    } else {
                      alert(`Print report: ${selectedReport.title}`);
                    }
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                >
                  <Printer className="w-4 h-4" />
                  Print
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Reports;
